import {
    Icon,
    IconButton,
} from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { DataTypeEnum } from '../../_common/enum/dataType.enum';
import { CommonTable } from '../../_components/table/common.table';
import { PaperTableHeader } from '../../_components/table/header.table';
import { readSearchStorage, SearchTableHeader } from '../../_components/table/search.table';
import { TABLE_FILTER } from '../../_const/tableFilter.const';
import { getDate } from '../../_helpers/dateTime.helper';
import { removeSpaces } from '../../_helpers/format.helper';
import { handleErrorResponse } from '../../_helpers/handleResponse.helper';
import { accountService } from '../../_services/accounts.service';
import { ticketService } from '../../_services/ticket.service';
import { TicketView } from './ticket-view';

const TICKETS_FILTER_STORAGE = "TmsTickets";

export function TicketsIndex() {
    const [data, setData] = useState<any>({ items: [], count: 10 });
    const [filter, setFilter] = useState<any>(() => ({
        ...TABLE_FILTER,
        order: "asc",
        orderBy: "dueDate",
        pageSize: 50,
        ...readSearchStorage(TICKETS_FILTER_STORAGE),
    }));
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        ticketService.getAllTicketsView(filter)
            .then(
                json => {
                    if (!cancelled) {
                        setData(json);
                    }
                },
                (error) => {
                    if (!cancelled) {
                        handleErrorResponse(error);
                    }
                })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [filter]);

    const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
        const baseColumns: MRT_ColumnDef<any>[] = [
            {
                id: 'actions',
                header: 'Actions',
                size: 80,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <IconButton
                        color="primary"
                        onClick={() => {
                            const ticketUrl = `/Case/${row.original.caseId}/Ticket/${row.original.id}/${removeSpaces(row.original.ticketType)}`;
                            window.open(ticketUrl, '_blank', 'noopener,noreferrer');
                        }}
                    >
                        <Icon>edit</Icon>
                    </IconButton>
                ),
            },
            { accessorKey: 'ticketReferenceNumber', header: 'Ticket No' },
            { accessorKey: 'subjectName', header: 'Subject' },

            { accessorKey: 'status', header: 'Status' },
            { accessorKey: 'action', header: 'Action' },
            { accessorKey: 'ticketType', header: 'Type' },
        ];

        const clientColumn: MRT_ColumnDef<any> = { accessorKey: 'client', header: 'Client' };

        const dueDateColumn: MRT_ColumnDef<any> = {
            accessorKey: 'dueDate',
            header: 'Due Date',
            accessorFn: (rowData: any) =>
                rowData.dueDate ? getDate(rowData.dueDate) : null,
        };

        const ldsColumn: MRT_ColumnDef<any> = {
            accessorKey: 'lds',
            header: 'LDS',
            accessorFn: (rowData: any) =>
                rowData.lds ? getDate(rowData.lds) : null,
        };

        const tailColumns: MRT_ColumnDef<any>[] = [
            { accessorKey: 'isDiscreet', header: 'RA/Discreet' },
            { accessorKey: 'ticketQuery', header: 'Query ?' },
            { accessorKey: 'formStatus', header: 'Form Status' },
        ];

        return [
            ...baseColumns,
            ...(accountService.isAdmin ? [clientColumn] : []),
            ...(accountService.isAgent ? [] : [dueDateColumn]),
            ...(accountService.isAdmin ? [ldsColumn] : []),
            ...tailColumns,
        ];
    }, []);

    const primaryFilterInputs = useMemo(() => [
        { label: "Ticket Number", name: "ticketReferenceNumber", onChange: setFilter, value: filter.ticketReferenceNumber },
        { label: "Subject", name: "subjectName", onChange: setFilter, value: filter.subjectName },
        { label: "Status", name: "statusId", dataType: DataTypeEnum.TicketStatuses, type: "multiple" as const, onChange: setFilter, value: filter.statusId },
        { label: "Action", name: "actionId", dataType: DataTypeEnum.ActionTypes, type: "multiple" as const, onChange: setFilter, value: filter.actionId },
        ...(!accountService.isAgent ? [
            { label: "Due Date", name: "dueDate", type: "date" as const, onChange: setFilter, value: filter.dueDate },
            { label: "LDS", name: "lds", type: "date" as const, onChange: setFilter, value: filter.lds },
            { label: "Next Action Date", name: "nextActionDate", type: "date" as const, onChange: setFilter, value: filter.nextActionDate },
        ] : []),
    ], [filter.ticketReferenceNumber, filter.subjectName, filter.statusId, filter.actionId, filter.dueDate, filter.lds, filter.nextActionDate]);

    const secondaryFilterInputs = useMemo(() => [
        { label: "Type", name: "ticketType", dataType: DataTypeEnum.TicketType, type: "multiple" as const, onChange: setFilter, value: filter.ticketType },
        {
            label: "Client", name: "clientId", dataType: DataTypeEnum.Clients, type: "multiple" as const, onChange: setFilter, value: filter.clientId,
            hidden: (accountService.isClient || accountService.isAgent)
        },
        {
            label: "Agent", name: "agentId", dataType: DataTypeEnum.Agents, type: "multiple" as const, onChange: setFilter, value: filter.agentId,
            hidden: (accountService.isClient || accountService.isAgent)
        },
        { label: "RA/Discreet", name: "isDiscreet", dataType: DataTypeEnum.YesNo, type: "autocomplete" as const, onChange: setFilter, value: filter.isDiscreet, hidden: true },
        { label: "Action List", name: "ticketActionListId", dataType: DataTypeEnum.AllTicketActionLists, type: "multiple" as const, groupBy: "group" as const, onChange: setFilter, value: filter.ticketActionListId },
        { label: "Query", name: "query", dataType: DataTypeEnum.YesNo, type: "autocomplete" as const, onChange: setFilter, value: filter.query },
        { label: "Form Status", name: "formStatus", dataType: DataTypeEnum.FormStatus, type: "multiple" as const, onChange: setFilter, value: filter.formStatus },
    ], [filter.ticketType, filter.clientId, filter.agentId, filter.isDiscreet, filter.ticketActionListId, filter.query, filter.formStatus]);


    return (
        <>
            <div style={{ width: isOpen ? `calc(100% - 530px)` : `100%`, }}>
                <PaperTableHeader title="Tickets" />
                <SearchTableHeader
                    equalWidth minItemWidth={140}
                    localStorageName={TICKETS_FILTER_STORAGE}
                    inputs={primaryFilterInputs}
                />

                <SearchTableHeader
                    equalWidth minItemWidth={140}
                    localStorageName={TICKETS_FILTER_STORAGE}
                    showClearButton
                    inputs={secondaryFilterInputs}
                />

                <CommonTable
                    columns={columns}
                    data={data}
                    filter={filter}
                    setFilter={setFilter}
                    loading={isLoading}
                />
            </div>

            <Routes>
                {/*<Route path="Case/:caseId/Ticket/:id/:ticketType/*" element={<TicketManageDrawer onSave={() => { navigate(""); fetchData(); }} onClose={() => { navigate(""); setIsOpen(false) }} />} />*/}
                <Route path="Case/:caseId/Ticket/:id/:ticketType" element={<TicketView />}></Route>

            </Routes>
        </>
    );
}
